import { Button, Modal, ModalClose } from "@govlaunch/core";
import React, { ReactElement, useState } from "react";
import { FormattedMessage } from "react-intl";
import { ICompanyAccountBillingInfoQuery } from "~/lib/queries/__generated__/CompanyAccountBillingInfoQuery.generated";
import Strongify from "~/lib/utils/strongify";

type TStripePlan = NonNullable<NonNullable<ICompanyAccountBillingInfoQuery["company"]>["stripePlan"]>;

interface IConfirmPlanChangeChildrenState {
  onRequestConfirmation: () => void;
}

interface IConfirmPlanChangeProps {
  toPlan: TStripePlan | null;
  onConfirm: () => void;
  children: (state: IConfirmPlanChangeChildrenState) => ReactElement;
}

export default function ConfirmPlanChange({ toPlan, children, onConfirm }: IConfirmPlanChangeProps) {
  const [isOpen, setIsOpen] = useState(false);

  const onRequestClose = () => setIsOpen(false);

  if (toPlan === null) {
    return children({
      onRequestConfirmation: () => {},
    });
  }

  return (
    <>
      <Modal isOpen={isOpen} ariaHideApp={false} onRequestClose={onRequestClose}>
        <div
          css={{
            display: "grid",
            gridRowGap: 20,
            width: 370,
            padding: 20,
          }}
        >
          <div
            css={{
              display: "flex",
              alignItems: "center",
            }}
          >
            <h2
              css={{
                fontWeight: "bold",
                margin: 0,
              }}
            >
              <FormattedMessage defaultMessage="Confirm Change" id="tzQB6V" />
            </h2>

            <ModalClose
              onClick={onRequestClose}
              style={{
                position: "relative",
                top: 0,
                right: 0,
                marginLeft: "auto",
                cursor: "pointer",
              }}
            />
          </div>

          <p
            css={{
              margin: 0,
              fontWeight: 400,
              fontSize: 14,
              lineHeight: 1.33,
            }}
          >
            <FormattedMessage
              defaultMessage="You're about to change your plan to <strong>{plan}</strong>."
              id="FpaUHD"
              values={{
                plan: toPlan.displayName,
                strong: (chunk: any) => Strongify(chunk),
              }}
            />
          </p>

          {toPlan.changeDetails && toPlan.changeDetails.prorateCost !== null && (
            <p
              css={{
                margin: 0,
                fontWeight: 400,
                fontSize: 14,
                lineHeight: 1.33,
              }}
            >
              <FormattedMessage
                defaultMessage="If you proceed, we'll charge <strong>{prorateCost}</strong> now."
                id="BJK59q"
                values={{
                  prorateCost: toPlan.changeDetails.prorateCost < 100 ? 0 : toPlan.changeDetails.prorateCost / 100,
                  strong: (chunk: any) => Strongify(chunk),
                }}
              />
            </p>
          )}

          <div
            css={{
              display: "grid",
              gridColumnGap: 20,
              gridTemplateColumns: "1fr 1fr",
            }}
          >
            <Button theme="link" color="danger" onClick={onRequestClose}>
              No. Don't change
            </Button>

            <Button
              onClick={() => {
                onConfirm();
                onRequestClose();
              }}
            >
              <FormattedMessage defaultMessage="Proceed" id="VNX4fn" />
            </Button>
          </div>
        </div>
      </Modal>

      {children({
        onRequestConfirmation: () => setIsOpen(true),
      })}
    </>
  );
}
